import { computed, inject, Injectable, signal } from '@angular/core';
import { catchError, firstValueFrom, from, Observable, of, tap, throwError } from 'rxjs';
import { customSwitchMap } from '@core/operators/customSwitchMap';
import { ConfigurationHttpService, RecaptchaBaseConfig } from '@core/api';

/* eslint-disable  @typescript-eslint/no-explicit-any */
declare const grecaptcha: any;

@Injectable({
  providedIn: 'root',
})
export class RecaptchaService {
  private readonly configService = inject(RecaptchaConfigService);
  private readonly directService = inject(DirectRecaptchaV3Service);

  readonly recaptchaToken = signal<string | null>(null);

  getToken(action: string = 'submit'): Observable<string | true> {
    const enabled = this.configService.recaptchaEnabled();
    const siteKey = this.configService.recaptchaSiteKey();

    if (!enabled || !siteKey) {
      return of(true);
    }

    if (this.configService.status() !== 'ready') {
      return throwError(() => new Error('Recaptcha not ready yet.'));
    }

    return from(this.directService.execute(siteKey, action)).pipe(
      tap(token => {
        this.recaptchaToken.set(token);
      }),
      customSwitchMap(),
    );
  }
}

export type RecaptchaStatus = 'loading' | 'ready' | 'error';

@Injectable({
  providedIn: 'root',
})
export class RecaptchaConfigService {
  private readonly configurationHttpService = inject(ConfigurationHttpService);
  private readonly config = signal<RecaptchaBaseConfig | null>(null);
  private readonly recaptchaStatus = signal<RecaptchaStatus>('loading');

  readonly status = this.recaptchaStatus.asReadonly();

  readonly recaptchaSiteKey = computed(() => {
    return this.config()?.enabled ? this.config()?.key : null;
  });

  readonly recaptchaEnabled = computed(() => {
    return this.config()?.enabled;
  });

  loadConfig(): Observable<RecaptchaBaseConfig> {
    return this.configurationHttpService.configurationCaptchaGet().pipe(
      customSwitchMap(),
      tap(config => {
        this.config.set(config);
        this.recaptchaStatus.set('ready');
      }),
      catchError(err => {
        console.error(err);
        this.recaptchaStatus.set('error');
        return of(null);
      }),
    );
  }
}

@Injectable({
  providedIn: 'root',
})
export class DirectRecaptchaV3Service {
  private scriptLoadedForKey: string | null = null;

  async init(siteKey: string): Promise<void> {
    await this.loadScript(siteKey);
  }

  async loadScript(siteKey: string): Promise<void> {
    if (this.scriptLoadedForKey === siteKey) {
      return Promise.resolve();
    }

    return new Promise(resolve => {
      const script = document.createElement('script');
      script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        this.scriptLoadedForKey = siteKey;
        resolve();
      };
      document.head.appendChild(script);
    });
  }

  async execute(siteKey: string, action: string): Promise<string> {
    return new Promise(resolve => {
      grecaptcha.ready(() => {
        grecaptcha.execute(siteKey, { action }).then(resolve);
      });
    });
  }
}

export function recaptchaConfigInitialize(): Promise<void> {
  const configService = inject(RecaptchaConfigService);
  return firstValueFrom(configService.loadConfig()).then(() => void 0);
}
